import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Mail, Plus, Ticket, UserCheck, Users } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, ErrorState, Field, Input, RelativeTime, Skeleton } from "@/design";
import { api, beginSignup, idem, prepareProofOfWork, problemOf, qk, type InvitePreview, type PreparedPow, type SignupBeginRequest, type SignupConfig } from "@/lib/api";
import { StepHeading } from "./OnboardingFrame";
import type { SignupDraft } from "./SignupFlow";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function EmailStep({
  draft,
  update,
  config,
  invite,
  joining,
  signedInAs,
  onSent,
  onNewWorkspace,
}: {
  draft: SignupDraft;
  update: (p: Partial<SignupDraft>) => void;
  config: SignupConfig & { legacy?: boolean };
  invite: { code: string; preview: UseQueryResult<InvitePreview> } | null;
  /** Signing up from a workspace invitation. */
  joining: boolean;
  signedInAs: string | null;
  onSent: () => void;
  onNewWorkspace: () => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [touched, setTouched] = useState(false);
  const [news, setNews] = useState(false);
  const emailOk = EMAIL.test(draft.email.trim());
  const bits = invite || config.legacy ? 0 : config.proof_of_work.difficulty_bits;
  // The bot check runs quietly while you type, so pressing the button feels instant.
  const pow = useRef<PreparedPow | null>(null);
  useEffect(() => {
    if (bits > 0 && !signedInAs) pow.current = prepareProofOfWork(bits);
  }, [bits, signedInAs]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!emailOk) return;
    setBusy(true);
    setError(null);
    try {
      const request: SignupBeginRequest = {
        email: draft.email.trim(),
        name: draft.name.trim() || undefined,
        invite_code: invite?.code,
        accept_terms: true,
        terms_version: config.terms.version,
        privacy_version: config.privacy.version,
        marketing_opt_in: news,
      };
      const prepared = pow.current;
      pow.current = null; // Each solution works once; a retry solves a fresh one.
      const v = await beginSignup(request, { legacy: config.legacy, powBits: bits, prepared });
      update({ verificationId: v.verification_id, code: "", challenge: null, email: request.email, request });
      onSent();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    await api.POST("/v1/auth/logout", { params: { header: idem() } }).catch(() => undefined);
    qc.removeQueries({ queryKey: qk.me() });
    window.location.reload();
  }

  if (signedInAs) {
    return (
      <div>
        <StepHeading title="You're already signed in">
          You're signed in as <span className="font-medium text-text">{signedInAs}</span>. There's no need for a second account to start something new.
        </StepHeading>
        <div className="mt-8 space-y-3">
          <Button asChild variant="primary" size="lg" className="w-full">
            <Link to="/">
              <UserCheck aria-hidden="true" />
              Go to my workspace
            </Link>
          </Button>
          <Button size="lg" className="w-full" icon={<Plus />} onClick={onNewWorkspace}>
            Create another workspace
          </Button>
        </div>
        <p className="mt-8 border-t border-border pt-5 text-center text-sm text-muted">
          Setting up for someone else?{" "}
          <button type="button" onClick={() => void signOut()} className="rounded-sm font-medium text-accent-text underline-offset-4 hover:underline">
            Sign out first
          </button>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      {invite && <InviteBanner code={invite.code} preview={invite.preview} />}
      {joining && (
        <p className="mb-6 flex items-start gap-3 rounded-lg border border-border bg-surface p-3.5 text-sm text-muted">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-text" aria-hidden="true">
            <Users className="size-[18px]" />
          </span>
          <span>You've been invited to join a team on Guv. Create your account and you'll go straight into their workspace.</span>
        </p>
      )}

      <StepHeading title={invite || joining ? "Create your account" : "Create your Guv account"}>No password to remember. We'll email you a code, then set up a passkey on this device.</StepHeading>

      <div className="mt-8 space-y-4">
        <Field label="Your name" hint="So your team knows who did what.">
          <Input autoComplete="name" value={draft.name} onChange={(e) => update({ name: e.target.value })} placeholder="Alex Morgan" autoFocus={!draft.name} />
        </Field>
        <Field label="Email" error={touched && !emailOk ? (draft.email ? "That doesn't look like an email address. Check for typos." : "Enter your email address.") : undefined}>
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            value={draft.email}
            onChange={(e) => update({ email: e.target.value })}
            onBlur={() => draft.email && setTouched(true)}
            placeholder="you@example.co.uk"
            leading={<Mail />}
          />
        </Field>
        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-muted">
          <input type="checkbox" checked={news} onChange={(e) => setNews(e.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]" />
          <span>Send me the occasional product update. No more than once a month; stop whenever you like.</span>
        </label>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          Email me a code
        </Button>
      </div>

      {error != null && <ErrorState error={error} className="mt-5" />}

      <p className="mt-6 text-xs leading-relaxed text-subtle">
        By continuing you agree to the{" "}
        <a href={config.terms.url} className="underline underline-offset-2 hover:text-text">
          terms
        </a>{" "}
        and{" "}
        <a href={config.privacy.url} className="underline underline-offset-2 hover:text-text">
          privacy notice
        </a>
        . Your account and your data stay in the UK.
      </p>
      <p className="mt-8 border-t border-border pt-5 text-center text-sm text-muted">
        Already have an account?{" "}
        <Link to="/login" className="rounded-sm font-medium text-accent-text underline-offset-4 hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  );
}

function InviteBanner({ code, preview }: { code: string; preview: UseQueryResult<InvitePreview> }) {
  return (
    <div className="mb-6 rounded-lg border border-border bg-surface p-3.5">
      {preview.isPending ? (
        <Skeleton className="h-9 w-full" />
      ) : preview.isError ? (
        <p className="text-sm text-danger-text" role="alert">
          <span className="font-medium">{problemOf(preview.error).title}.</span> {problemOf(preview.error).fix ?? "Check the code in your invitation email."}
        </p>
      ) : (
        <p className="flex items-start gap-3 text-sm text-muted">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-text" aria-hidden="true">
            <Ticket className="size-[18px]" />
          </span>
          <span>
            <span className="font-medium text-text">Your invitation is ready.</span> Code <span className="font-mono text-text">{code}</span>
            {preview.data.email_hint ? <> for {preview.data.email_hint}</> : null}, expires <RelativeTime value={preview.data.expires_at} />.
          </span>
        </p>
      )}
    </div>
  );
}
